import { useState, useEffect } from "react";
import { usePlaidLink } from "react-plaid-link";
import { useTheme } from "../context/ThemeContext";
import { getBalances, syncPlaid, getDebts, getInvestmentHoldings, createUpdateLinkToken } from "../api/client";

const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AccountCard = ({ account, COLORS }) => {
  const balance = parseFloat(account.balance || 0);
  const isCredit = account.type === "credit";
  const isSavings = account.show_as_savings || ["savings","money market","cd","hsa"].includes(account.subtype);
  const balanceColor = isCredit ? COLORS.red : COLORS.green;
  const typeLabel = isCredit ? "Credit" : isSavings ? "Savings" : account.subtype?.charAt(0).toUpperCase() + account.subtype?.slice(1) || account.type;

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <div>
        <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{account.custom_name || account.name}</div>
        <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: "capitalize" }}>{typeLabel}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ color: balanceColor, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{isCredit ? "-" : ""}{fmt(balance)}</div>
        {account.last_synced && (
          <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 2 }}>
            {new Date(account.last_synced).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </div>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ title, total, totalColor, COLORS }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
    <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 14, margin: 0 }}>{title}</h3>
    <span style={{ color: totalColor, fontFamily: "monospace", fontWeight: 700, fontSize: 14 }}>{fmt(total)}</span>
  </div>
);

const DebtCard = ({ debt, COLORS }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
    <div>
      <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{debt.name}</div>
      <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: "capitalize" }}>{(debt.debt_type || "debt").replace(/_/g, " ")}</div>
    </div>
    <div style={{ color: COLORS.red, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>-{fmt(parseFloat(debt.balance || 0))}</div>
  </div>
);

// Single handler for enabling Investments consent — currently update-mode Link.
// Swap body later if Robinhood needs full disconnect/reconnect.
const EnableInvestmentsButton = ({ itemId, onDone, COLORS, label = "Reconnect to enable Investments" }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const start = async () => {
    setLoading(true);
    try {
      const res = await createUpdateLinkToken(itemId);
      const token = res.data?.link_token || res.data?.data?.link_token;
      if (!token) throw new Error("No update link token");
      setLinkToken(token);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async () => {
      try {
        await syncPlaid(true);
        onDone?.();
      } catch (e) { console.error(e); }
      finally { setLoading(false); setLinkToken(null); }
    },
    onExit: () => { setLoading(false); setLinkToken(null); },
  });

  useEffect(() => {
    if (ready && linkToken) open();
  }, [ready, linkToken, open]);

  return (
    <button
      type="button"
      onClick={start}
      disabled={loading}
      style={{
        background: "transparent", border: "none", padding: 0, marginTop: 6,
        color: COLORS.accent, fontSize: 12, fontWeight: 700, cursor: loading ? "wait" : "pointer",
        fontFamily: "inherit", textAlign: "left",
      }}
    >
      {loading ? "Opening Plaid…" : label}
    </button>
  );
};

const HoldingRow = ({ h, COLORS }) => {
  const label = h.ticker || h.name;
  const showType = h.type && !["equity", "etf", "mutual fund"].includes(String(h.type).toLowerCase());
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <div style={{ minWidth: 0, paddingRight: 10 }}>
        <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
          {showType && (
            <span style={{
              marginLeft: 6, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
              color: COLORS.textMuted, border: `1px solid ${COLORS.border}`, borderRadius: 4, padding: "1px 5px",
            }}>{h.type}</span>
          )}
        </div>
        {h.ticker && h.name && h.name !== h.ticker && (
          <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</div>
        )}
      </div>
      <div style={{ color: COLORS.green, fontFamily: "monospace", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{fmt(h.institutionValue)}</div>
    </div>
  );
};

const InvestmentAccountBlock = ({ account, holdingsAccount, COLORS, onEnableInvestments }) => {
  const balance = parseFloat(account.balance || 0);
  const status = holdingsAccount?.investmentsStatus || null;
  const itemId = account.item_id || holdingsAccount?.itemId || null;
  const holdings = holdingsAccount?.holdings || [];
  const cash = holdings.filter((h) => String(h.type || "").toLowerCase() === "cash");
  const positions = holdings.filter((h) => String(h.type || "").toLowerCase() !== "cash");

  let emptyMsg = null;
  if (!holdings.length) {
    if (status === "not_ready") emptyMsg = "Holdings will appear after the next sync";
    else if (status === "not_consented") emptyMsg = "Reconnect to enable Investments";
    else if (status === "error") emptyMsg = "Holdings temporarily unavailable";
    else if (status === "unsupported") emptyMsg = null;
    else if (status === "ok") emptyMsg = "No holdings reported";
    else emptyMsg = "Holdings will appear after the next sync";
  }

  // unsupported with no holdings: still show the account balance row, no empty copy
  if (status === "unsupported" && !holdings.length) {
    return <AccountCard account={account} COLORS={COLORS} />;
  }

  return (
    <div style={{ padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{account.custom_name || account.name}</div>
          <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: "capitalize" }}>{account.subtype || "Investment"}</div>
        </div>
        <div style={{ color: COLORS.green, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{fmt(balance)}</div>
      </div>

      {positions.length > 0 && (
        <div style={{ marginTop: 8, paddingLeft: 4 }}>
          {positions.map((h) => <HoldingRow key={h.id} h={h} COLORS={COLORS} />)}
        </div>
      )}
      {cash.length > 0 && (
        <div style={{ marginTop: 8, paddingLeft: 4 }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Cash</div>
          {cash.map((h) => <HoldingRow key={h.id} h={h} COLORS={COLORS} />)}
        </div>
      )}

      {emptyMsg && (
        <div style={{ marginTop: 8 }}>
          <div style={{ color: COLORS.textMuted, fontSize: 12 }}>{emptyMsg}</div>
          {status === "not_consented" && itemId && (
            <EnableInvestmentsButton
              itemId={itemId}
              onDone={onEnableInvestments}
              COLORS={COLORS}
            />
          )}
        </div>
      )}
    </div>
  );
};

const Accounts = () => {
  const { COLORS } = useTheme();
  const [balances, setBalances] = useState(null);
  const [debts, setDebts] = useState([]);
  const [holdingsData, setHoldingsData] = useState({ accounts: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getBalances();
      setBalances(r.data);
    } catch (e) { console.error(e); }
    try {
      const r = await getDebts();
      setDebts(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error(e); }
    try {
      const r = await getInvestmentHoldings();
      setHoldingsData(r.data || { accounts: [], items: [] });
    } catch (e) {
      // Endpoint/migration may not be live yet — Accounts still works on balances alone.
      console.error(e);
      setHoldingsData({ accounts: [], items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try { await syncPlaid(true); await load(); } catch (e) {}
    finally { setSyncing(false); }
  };

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: "center", paddingTop: 80 }}>Loading...</div>
  );

  const allAccounts = balances?.accounts || [];

  const checkingAccs   = allAccounts.filter(a => a.type === "depository" && !a.show_as_savings && ["checking","prepaid"].includes(a.subtype));
  const savingsAccs    = allAccounts.filter(a => a.type === "depository" && (a.show_as_savings || ["savings","money market","cd","hsa","cash management"].includes(a.subtype)));
  const creditAccs     = allAccounts.filter(a => a.type === "credit");
  const investmentAccs = allAccounts.filter(a => a.type === "investment");

  const totalCash     = checkingAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  const totalSavings  = savingsAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  const totalDebt     = creditAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);
  // Net worth uses account balances ONLY — never sum holdings (would double-count).
  const totalInvest   = investmentAccs.reduce((s,a) => s + parseFloat(a.balance||0), 0);

  const holdingsByAccountId = Object.fromEntries(
    (holdingsData.accounts || []).map((a) => [a.accountId, a])
  );

  const unlinkedDebts = debts.filter(d => !d.account_id);
  const totalManualDebt = unlinkedDebts.reduce((s,d) => s + parseFloat(d.balance||0), 0);
  const totalDebtAll  = totalDebt + totalManualDebt;
  const netWorth      = totalCash + totalSavings + totalInvest - totalDebtAll;

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ padding: "20px 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Accounts</h2>
        <button onClick={handleSync} disabled={syncing} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", color: COLORS.textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {syncing ? "Syncing..." : "Sync"}
        </button>
      </div>

      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 10 }}>

        <div style={{ background: netWorth >= 0 ? COLORS.greenGlow : COLORS.redGlow, border: `1px solid ${netWorth >= 0 ? COLORS.green : COLORS.red}44`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Net Worth</div>
          <div style={{ color: netWorth >= 0 ? COLORS.green : COLORS.red, fontSize: 32, fontWeight: 800, fontFamily: "monospace", letterSpacing: -1 }}>
            {netWorth < 0 ? "-" : ""}{fmt(Math.abs(netWorth))}
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 4 }}>Assets minus debt</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Cash</div>
            <div style={{ color: COLORS.green, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalCash)}</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Savings</div>
            <div style={{ color: COLORS.green, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalSavings)}</div>
          </div>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Debt</div>
            <div style={{ color: COLORS.red, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>-{fmt(totalDebtAll)}</div>
          </div>
          {investmentAccs.length > 0 && (
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>Investments</div>
              <div style={{ color: COLORS.green, fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalInvest)}</div>
            </div>
          )}
        </div>

        {checkingAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Checking" total={totalCash} totalColor={COLORS.green} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Spending accounts</div>
            {checkingAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

        {savingsAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Savings" total={totalSavings} totalColor={COLORS.green} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Savings & money market</div>
            {savingsAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

        {creditAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Credit Cards" total={totalDebt} totalColor={COLORS.red} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Current balances</div>
            {creditAccs.map((a, i) => (
              <AccountCard key={i} account={a} COLORS={COLORS} />
            ))}
          </div>
        )}

        {investmentAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Investments" total={totalInvest} totalColor={COLORS.green} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Retirement & brokerage — holdings are detail only (not double-counted in net worth)</div>
            {investmentAccs.map((a) => (
              <InvestmentAccountBlock
                key={a.id}
                account={a}
                holdingsAccount={holdingsByAccountId[a.id]}
                COLORS={COLORS}
                onEnableInvestments={load}
              />
            ))}
          </div>
        )}

        {unlinkedDebts.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <SectionHeader title="Other Debts" total={totalManualDebt} totalColor={COLORS.red} COLORS={COLORS} />
            <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 10 }}>Manually tracked, not linked to a synced account</div>
            {unlinkedDebts.map((d, i) => (
              <DebtCard key={i} debt={d} COLORS={COLORS} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default Accounts;
